import React, { useState, useEffect } from 'react';
import { Trophy, RotateCcw, Star } from 'lucide-react';
import { playSound } from '../../utils/soundManager';

const MemoryGame = ({ onComplete, difficulty = 'easy' }) => {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedCards, setMatchedCards] = useState([]);
  const [moves, setMoves] = useState(0);
  const [gameWon, setGameWon] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Card sets based on difficulty
  const cardSets = {
    easy: ['🍎', '🍌', '🍊', '🍇', '🍓', '🍉'],
    medium: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼'],
    hard: ['⚽', '🏀', '🎾', '🏐', '🏈', '⚾', '🎱', '🏓', '🏸', '🥎']
  };

  useEffect(() => {
    initializeGame();
  }, [difficulty]);

  useEffect(() => {
    let interval;
    if (isPlaying && !gameWon) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameWon]);

  useEffect(() => {
    if (matchedCards.length > 0 && matchedCards.length === cards.length) {
      setGameWon(true);
      setIsPlaying(false);
      playSound('success');
      
      // Calculate score
      const score = Math.max(100 - moves * 2, 20);
      const timeBonus = Math.max(50 - Math.floor(timer / 2), 0);
      const totalScore = score + timeBonus;
      
      if (onComplete) {
        onComplete({ 
          completed: true,
          moves, 
          time: timer, 
          score: matchedCards.length / 2 // 1 XP per matched pair
        });
      }
    }
  }, [matchedCards, cards.length]);

  const initializeGame = () => {
    const selectedEmojis = cardSets[difficulty];
    const gameCards = [...selectedEmojis, ...selectedEmojis]
      .sort(() => Math.random() - 0.5)
      .map((emoji, index) => ({
        id: index,
        emoji,
        isFlipped: false,
        isMatched: false
      }));
    
    setCards(gameCards);
    setFlippedCards([]);
    setMatchedCards([]);
    setMoves(0);
    setTimer(0);
    setGameWon(false);
    setIsPlaying(false);
  };

  const handleCardClick = (cardId) => {
    if (!isPlaying) setIsPlaying(true);
    
    const card = cards.find(c => c.id === cardId);
    
    // Ignore if card is already flipped or matched
    if (flippedCards.includes(cardId) || matchedCards.includes(cardId)) {
      return;
    }

    // Ignore if two cards are already flipped
    if (flippedCards.length === 2) {
      return;
    }

    playSound('click');
    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    // Check for match when two cards are flipped
    if (newFlippedCards.length === 2) {
      setMoves(moves + 1);
      const [firstId, secondId] = newFlippedCards;
      const firstCard = cards.find(c => c.id === firstId);
      const secondCard = cards.find(c => c.id === secondId);

      if (firstCard.emoji === secondCard.emoji) {
        // Match found
        playSound('correct');
        setMatchedCards([...matchedCards, firstId, secondId]);
        setFlippedCards([]);
      } else {
        // No match
        playSound('incorrect');
        setTimeout(() => {
          setFlippedCards([]);
        }, 1000);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getGridClass = () => {
    if (difficulty === 'easy') return 'memory-grid-easy';
    if (difficulty === 'medium') return 'memory-grid-medium';
    return 'memory-grid-hard';
  };

  return (
    <div className="memory-game">
      <div className="memory-game-header">
        <div className="game-stats">
          <div className="stat-item">
            <span className="stat-label">Moves:</span>
            <span className="stat-value">{moves}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Time:</span>
            <span className="stat-value">{formatTime(timer)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Matched:</span>
            <span className="stat-value">{matchedCards.length / 2}/{cards.length / 2}</span>
          </div>
        </div>
        
        <button 
          className="reset-button hover-lift"
          onClick={initializeGame}
        >
          <RotateCcw size={20} />
          Reset
        </button>
      </div>

      <div className={`memory-grid ${getGridClass()}`}>
        {cards.map(card => (
          <div
            key={card.id}
            className={`memory-card ${
              flippedCards.includes(card.id) || matchedCards.includes(card.id)
                ? 'flipped'
                : ''
            } ${matchedCards.includes(card.id) ? 'matched' : ''}`}
            onClick={() => handleCardClick(card.id)}
          >
            <div className="card-inner">
              <div className="card-front">?</div>
              <div className="card-back">{card.emoji}</div>
            </div>
          </div>
        ))}
      </div>

      {gameWon && (
        <div className="game-won-overlay">
          <div className="game-won-modal animate-scaleIn">
            <Trophy size={60} color="#f39c12" />
            <h2>🎉 Congratulations!</h2>
            <p>You won the game!</p>
            <div className="win-stats">
              <div className="win-stat">
                <Star size={20} color="#f39c12" />
                <span>Moves: {moves}</span>
              </div>
              <div className="win-stat">
                <Star size={20} color="#f39c12" />
                <span>Time: {formatTime(timer)}</span>
              </div>
            </div>
            <button 
              className="play-again-button hover-lift"
              onClick={initializeGame}
            >
              Play Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryGame;
